import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import posthog from 'posthog-js';
import { resetPageSeoTags } from '@/composables/usePageSeo';
import { PAGE_SEO } from '@/helpers/pageSeo';
import { bootIntercom } from '@/helpers/intercom';

const testRoutes = {
	path: '/tests',
	children: [
		{
			path: 'feature-flag',
			component: () => import('@/screens/tests/FeatureFlags.vue'),
			meta: {
				requiresAuth: true,
			},
		},
	],
};

const dashboardRoutes: RouteRecordRaw = {
	path: '/dashboard',
	name: 'teacherDashboard',
	component: () => import('@/screens/HomeView.vue'),
	meta: {
		requiresAuth: true,
	},
	children: [
		{
			path: '',
			redirect: (to) => ({ name: 'get-started', params: to.params }),
		},
		{
			path: 'get-started',
			name: 'get-started',
			component: () => import('@/screens/dashboard/GettingStarted.vue'),
		},
		{
			path: 'professional-dev',
			component: () => import('@/screens/dashboard/ProfessionalDevelopment.vue'),
		},
		{
			path: 'book-a-meeting',
			component: () => import('@/screens/dashboard/BookMeeting.vue'),
		},
		{
			path: 'my-classes',
			name: 'my-classes',
			component: () => import('@/screens/dashboard/MyClasses.vue'),
		},
		{
			path: 'library',
			end: true,
			children: [
				{
					path: '',
					name: 'library',
					end: true,
					component: () => import('@/screens/dashboard/ProjectsLibrary.vue'),
				},
			],
		},
		{
			path: 'my-classes/details/:id',
			component: () => import('@/screens/dashboard/myclasses/Index.vue'),
			children: [
				{
					path: '',
					redirect: (to) => ({ name: 'students', params: to.params }),
				},
				{
					path: 'students',
					name: 'students',
					component: () => import('@/screens/dashboard/myclasses/Students.vue'),
				},
				{
					path: 'assignments',
					name: 'assignments',
					component: () => import('@/screens/dashboard/myclasses/Assignments.vue'),
				},
				{
					path: 'submissions',
					name: 'submissions',
					component: () => import('@/screens/dashboard/myclasses/SubmissionsTab.vue'),
				},
				{
					path: 'progress',
					name: 'progress',
					component: () => import('@/screens/dashboard/myclasses/Progress.vue'),
				},
				{
					path: 'teachers',
					name: 'teachers',
					component: () => import('@/screens/dashboard/myclasses/Teachers.vue'),
				},
			],
		},
		{
			path: 'my-showcases',
			children: [
				{
					path: '',
					name: 'my-showcases',
					end: true,
					component: () => import('@/screens/dashboard/MyShowcases.vue'),
				},
				{
					path: 'create',
					name: 'create-showcase',
					component: () => import('@/screens/dashboard/ShowcaseEditorPage.vue'),
				},
				{
					path: 'edit/:id',
					name: 'edit-showcase',
					component: () => import('@/screens/dashboard/ShowcaseEditorPage.vue'),
				},
			],
		},
	],
};

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	scrollBehavior: function (to, from, savedPosition) {
		if (to.hash) {
			return { el: to.hash };
		}
		return savedPosition ?? { top: 0 };
	},
	routes: [
		{
			path: '/',
			redirect: '/dashboard',
		},
		{
			path: '/login',
			name: 'login-start',
			component: () => import('@/screens/auth/Junction.vue'),
			meta: {
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/login',
			redirect: '/login/teacher',
		},
		{
			path: '/login/teacher',
			name: 'login-teacher',
			component: () => import('@/screens/auth/LoginTeacher.vue'),
			meta: {
				notAuth: true,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/signup',
			redirect: '/sign-up',
		},
		{
			path: '/sign-up',
			name: 'sign-up',
			component: () => import('@/screens/auth/SignUpView.vue'),
			meta: {
				clearCredentials: true,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/invite-co-teacher',
			name: 'invite-co-teacher',
			component: () => import('@/screens/auth/InviteCoTeacherView.vue'),
			meta: {
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/invite/:code',
			name: 'invite-landing',
			component: () => import('@/screens/auth/InviteLandingView.vue'),
			meta: {
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/forgot-password',
			redirect: '/login/teacher/forgot-password',
		},
		{
			path: '/login/teacher/forgot-password',
			name: 'forgotPassword',
			component: () => import('@/screens/auth/ForgotPasswordView.vue'),
			meta: {
				clearCredentials: true,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/reset-password',
			redirect: '/login/teacher/reset-password',
		},
		{
			path: '/login/teacher/reset-password',
			name: 'reset-password',
			component: () => import('@/screens/auth/ChangePasswordView.vue'),
			meta: {
				clearCredentials: true,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/kyc',
			name: 'kyc',
			component: () => import('@/screens/kyc/KycView.vue'),
			meta: {
				requiresAuth: true,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/verify-email',
			name: 'verify-email',
			component: () => import('@/screens/auth/VerifyEmailView.vue'),
			meta: {
				requiresAuth: true,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/verify-user-email',
			name: 'finalize-verify-email',
			component: () => import('@/screens/auth/FinalizeEmailVerification.vue'),
			meta: {
				requiresAuth: true,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/confirm-account-link',
			name: 'confirm-account-link',
			component: () => import('@/screens/auth/ConfirmAccountLinkView.vue'),
			meta: {
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/google/authorized',
			name: 'oauth-google-authorized',
			component: () => import('@/screens/auth/GoogleAuthorized.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/google/finalizeOauthFlow',
			name: 'oauth-google-finalized',
			component: () => import('@/screens/auth/GoogleFinalize.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/microsoft/authorized',
			name: 'oauth-microsoft-authorized',
			component: () => import('@/screens/auth/MicrosoftAuthorized.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/microsoft/finalizeOauthFlow',
			name: 'oauth-microsoft-finalized',
			component: () => import('@/screens/auth/MicrosoftFinalize.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/clever/authorized',
			name: 'oauth-clever-authorized',
			component: () => import('@/screens/auth/CleverAuthorized.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/clever/finalizeOauthFlow',
			name: 'oauth-clever-finalized',
			component: () => import('@/screens/auth/CleverFinalize.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/classlink/authorized',
			name: 'oauth-classlink-authorized',
			component: () => import('@/screens/auth/ClassLinkAuthorized.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/oauth/classlink/finalizeOauthFlow',
			name: 'oauth-classlink-finalized',
			component: () => import('@/screens/auth/ClassLinkFinalize.vue'),
			meta: {
				requiresAuth: false, // OAuth callback - backend validates via session state
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/dashboard/library/:id',
			component: () => import('@/screens/HomeViewProjectDetails.vue'),
			meta: {
				requiresAuth: true,
			},
			children: [
				{
					path: '',
					name: 'project-details-overview',
					component: () => import('@/screens/projects_library/OverviewTab.vue'),
					meta: {
						requiresAuth: true,
					},
				},
				{
					path: 'prepare',
					name: 'project-details-prepare',
					component: () => import('@/screens/projects_library/PrepareTab.vue'),
					meta: {
						requiresAuth: true,
					},
				},
				{
					path: 'prepare/:lessonSlug',
					name: 'project-details-prepare-lesson-details',
					component: () => import('@/screens/projects_library/LessonDetails.vue'),
					meta: {
						requiresAuth: true,
					},
				},
				{
					path: 'run-in-class',
					name: 'project-details-run',
					component: () => import('@/screens/projects_library/RunInClassTab.vue'),
					meta: {
						requiresAuth: true,
					},
				},
			],
		},
		{
			path: '/catalog-2025',
			name: 'catalog',
			component: () => import('@/screens/catalog/2025.vue'),
			meta: {
				requiresAuth: false,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/request-a-quote-2025',
			name: 'request-a-quote',
			component: () => import('@/screens/request_a_quote/2025.vue'),
			meta: {
				requiresAuth: false,
				allowedOnSmallScreens: true,
			},
		},
		{
			path: '/leaderboard/:classId/:lessonId',
			name: 'leaderboard-fullscreen',
			component: () => import('@/screens/dashboard/LeaderboardFullscreen.vue'),
			meta: {
				requiresAuth: true,
				allowedOnSmallScreens: true,
			},
		},
		dashboardRoutes,
		testRoutes,
		{
			path: '/showcases',
			children: [
				{
					path: 'view/sample',
					name: 'sample-showcase',
					component: () => import('@/screens/dashboard/SampleShowcase.vue'),
				},
				{
					path: 'view/:id',
					name: 'showcase',
					end: true,
					component: () => import('@/screens/dashboard/Showcase.vue'),
				},
			],
		},
		{
			// This needs to be the last defined route as it matches everything that isn't matched by any other route
			// Refer to https://router.vuejs.org/guide/essentials/dynamic-matching#Catch-all-404-Not-found-Route to understand
			// how this works
			path: '/:pathMatch(.*)*',
			redirect: '/',
		},
	],
});

router.beforeEach(async (to, from, next) => {
	const authStore = useAuthStore();
	const userStore = useUserStore();
	userStore.loadCredentials();
	if (to.meta.requiresAuth) {
		if (authStore.isUserSignedIn && authStore.isAccessTokenValid) {
			if (!userStore.user?.id) {
				await userStore.refreshUserDetails();

				const userEmail = userStore.user!.email;
				posthog.identify(userStore.user!.id, {
					environment: import.meta.env.VITE_MODE,
					email: userEmail,
					name: userStore.user!.name,
					kyc_details: userStore.user!.profile,
				});
				posthog.setPersonPropertiesForFlags({ email: userEmail });
				const internalUserDomains = [/qweebi\.com/i, /example\.com/i, /mailinator\.com/i];
				if (internalUserDomains.some((domain) => domain.test(userEmail))) {
					posthog.stopSessionRecording();
				}
			}

			if (!userStore.user?.profile) {
				if (to.name === 'kyc' || to.name === 'verify-email' || to.name === 'finalize-verify-email') {
					bootIntercom(userStore.user);
					next();
				} else {
					next('/dashboard/kyc');
				}
			} else if (userStore.user.emailVerificationStatus === 'NOT_VERIFIED') {
				if (to.name === 'verify-email' || to.name === 'finalize-verify-email') {
					bootIntercom(userStore.user);
					next();
				} else {
					next('/dashboard/verify-email');
				}
			} else {
				bootIntercom(userStore.user);
				next();
			}
		} else {
			void userStore.logOut();
			if (to.fullPath !== '/') {
				next({
					name: 'login-start',
					query: {
						redirect: encodeURIComponent(to.fullPath),
					},
				});
			} else {
				next('/login');
			}
		}
	} else if (to.meta.clearCredentials) {
		void userStore.logOut();
		next();
	} else if (to.meta.notAuth) {
		if (authStore.isUserSignedIn && authStore.isAccessTokenValid) {
			next('/dashboard');
		} else {
			void userStore.logOut();
			next();
		}
	} else {
		next();
	}
});

router.afterEach((to) => {
	if (!(to.path in PAGE_SEO)) {
		resetPageSeoTags();
	}

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: Intercom may not be loaded
	if (window.Intercom) {
		window.Intercom('update', {
			custom_launcher_selector: '#buttonShowIntercom',
			hide_default_launcher: true,
		});
	}
});

export default router;
